import { useState, useEffect } from "react";
import api from "@/api";
import { toast } from "sonner";
import {
  X, Plus, Trash2, GripVertical, CheckCircle, AlertTriangle,
  HelpCircle, ListChecks, MessageSquare, Save
} from "lucide-react";

/**
 * Questionnaire Editor Component
 * Used in Admin to create/edit questionnaires for educational content
 */
export default function QuestionnaireEditor({ contentId, contentTitle, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [questionnaires, setQuestionnaires] = useState({ before: null, after: null });
  const [activeTab, setActiveTab] = useState("AFTER");
  const [form, setForm] = useState({
    title: "",
    description: "",
    isRequired: false,
    questions: []
  });

  useEffect(() => {
    fetchQuestionnaires();
  }, [contentId]);

  const fetchQuestionnaires = async () => {
    try {
      const res = await api.get(`/education/admin/questionnaires?content_id=${contentId}`);
      const data = res.data;
      
      const before = data.find(q => q.stage === "BEFORE");
      const after = data.find(q => q.stage === "AFTER");
      
      setQuestionnaires({ before, after });
      
      // Load the active tab's questionnaire into form
      const active = activeTab === "BEFORE" ? before : after;
      if (active) {
        setForm({
          title: active.title || "",
          description: active.description || "",
          isRequired: active.isRequired || false,
          questions: active.questions || []
        });
      } else {
        resetForm();
      }
    } catch (err) {
      // error handled by toast
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      title: activeTab === "AFTER" ? "Quiz de compréhension" : "Questions préalables",
      description: "",
      isRequired: false,
      questions: []
    });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const questionnaire = tab === "BEFORE" ? questionnaires.before : questionnaires.after;
    if (questionnaire) {
      setForm({
        title: questionnaire.title || "",
        description: questionnaire.description || "",
        isRequired: questionnaire.isRequired || false,
        questions: questionnaire.questions || []
      });
    } else {
      resetForm();
    }
  };

  const addQuestion = () => {
    const newQuestion = {
      id: `q${Date.now()}`,
      text: "",
      type: "SINGLE_CHOICE",
      options: ["Oui", "Non"],
      correctAnswer: null,
      alertOnAnswer: null,
      required: true
    };
    setForm({ ...form, questions: [...form.questions, newQuestion] });
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...form.questions];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, questions: updated });
  };

  const removeQuestion = (index) => {
    const updated = form.questions.filter((_, i) => i !== index);
    setForm({ ...form, questions: updated });
  };

  const addOption = (questionIndex) => {
    const updated = [...form.questions];
    updated[questionIndex].options = [...updated[questionIndex].options, ""];
    setForm({ ...form, questions: updated });
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    const updated = [...form.questions];
    updated[questionIndex].options[optionIndex] = value;
    setForm({ ...form, questions: updated });
  };

  const removeOption = (questionIndex, optionIndex) => {
    const updated = [...form.questions];
    updated[questionIndex].options = updated[questionIndex].options.filter((_, i) => i !== optionIndex);
    // Clear correctAnswer/alertOnAnswer if they match the removed option
    const removedOption = form.questions[questionIndex].options[optionIndex];
    if (updated[questionIndex].correctAnswer === removedOption) {
      updated[questionIndex].correctAnswer = null;
    }
    if (updated[questionIndex].alertOnAnswer === removedOption) {
      updated[questionIndex].alertOnAnswer = null;
    }
    setForm({ ...form, questions: updated });
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Le titre est obligatoire");
      return;
    }
    if (form.questions.length === 0) {
      toast.error("Ajoutez au moins une question");
      return;
    }
    // Validate questions
    for (let i = 0; i < form.questions.length; i++) {
      if (!form.questions[i].text.trim()) {
        toast.error(`Question ${i + 1}: Le texte est obligatoire`);
        return;
      }
      if (form.questions[i].options.length < 2) {
        toast.error(`Question ${i + 1}: Ajoutez au moins 2 options`);
        return;
      }
    }

    setSaving(true);
    try {
      const existing = activeTab === "BEFORE" ? questionnaires.before : questionnaires.after;
      
      const payload = {
        contentId,
        title: form.title,
        description: form.description || null,
        stage: activeTab,
        isRequired: form.isRequired,
        questions: form.questions.map(q => ({
          id: q.id,
          text: q.text,
          type: q.type,
          options: q.options.filter(o => o.trim()),
          correctAnswer: q.correctAnswer || null,
          alertOnAnswer: q.alertOnAnswer || null,
          required: q.required
        }))
      };

      if (existing) {
        await api.put(`/education/admin/questionnaires/${existing.id}`, payload);
        toast.success("Questionnaire mis à jour");
      } else {
        await api.post("/education/admin/questionnaires", payload);
        toast.success("Questionnaire créé");
      }
      
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const existing = activeTab === "BEFORE" ? questionnaires.before : questionnaires.after;
    if (!existing) return;
    
    if (!confirm("Supprimer ce questionnaire ?")) return;
    
    try {
      await api.delete(`/education/admin/questionnaires/${existing.id}`);
      toast.success("Questionnaire supprimé");
      setQuestionnaires({
        ...questionnaires,
        [activeTab === "BEFORE" ? "before" : "after"]: null
      });
      resetForm();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8">
          <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  const currentQuestionnaire = activeTab === "BEFORE" ? questionnaires.before : questionnaires.after;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-8 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-5 border-b bg-gradient-to-r from-blue-50 to-indigo-50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <ListChecks className="w-5 h-5 text-blue-600" />
                Questionnaires
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Contenu: {contentTitle}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => handleTabChange("BEFORE")}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                activeTab === "BEFORE"
                  ? "bg-white text-blue-700 shadow"
                  : "text-slate-600 hover:bg-white/50"
              }`}
            >
              Avant le contenu
              {questionnaires.before && (
                <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                  {questionnaires.before.questionCount}
                </span>
              )}
            </button>
            <button
              onClick={() => handleTabChange("AFTER")}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                activeTab === "AFTER"
                  ? "bg-white text-blue-700 shadow"
                  : "text-slate-600 hover:bg-white/50"
              }`}
            >
              Après le contenu
              {questionnaires.after && (
                <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                  {questionnaires.after.questionCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Questionnaire info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Titre du questionnaire *</label>
              <input
                type="text"
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                placeholder="Ex: Quiz de compréhension"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Description</label>
              <input
                type="text"
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                placeholder="Ex: Vérifiez votre compréhension"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isRequired}
              onChange={e => setForm({ ...form, isRequired: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-slate-600">Questionnaire obligatoire</span>
          </label>

          {/* Questions */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-700">Questions ({form.questions.length})</h3>
              <button
                onClick={addQuestion}
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100"
              >
                <Plus className="w-4 h-4" />
                Ajouter
              </button>
            </div>

            {form.questions.length === 0 ? (
              <div className="text-center py-8 text-slate-400 border-2 border-dashed border-slate-200 rounded-xl">
                <HelpCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>Aucune question</p>
                <button
                  onClick={addQuestion}
                  className="mt-2 text-blue-600 text-sm font-medium hover:underline"
                >
                  Ajouter une question
                </button>
              </div>
            ) : (
              form.questions.map((question, qIndex) => (
                <QuestionCard
                  key={question.id}
                  question={question}
                  index={qIndex}
                  onUpdate={(field, value) => updateQuestion(qIndex, field, value)}
                  onRemove={() => removeQuestion(qIndex)}
                  onAddOption={() => addOption(qIndex)}
                  onUpdateOption={(optIndex, value) => updateOption(qIndex, optIndex, value)}
                  onRemoveOption={(optIndex) => removeOption(qIndex, optIndex)}
                />
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-slate-50 flex justify-between items-center flex-shrink-0">
          <div>
            {currentQuestionnaire && (
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium"
              >
                <Trash2 className="w-4 h-4 inline mr-1" />
                Supprimer
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-white"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {currentQuestionnaire ? "Mettre à jour" : "Créer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Question Card Component
function QuestionCard({ question, index, onUpdate, onRemove, onAddOption, onUpdateOption, onRemoveOption }) {
  return (
    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">
            {index + 1}
          </span>
          <select
            value={question.type}
            onChange={e => onUpdate("type", e.target.value)}
            className="text-xs px-2 py-1 border border-slate-200 rounded bg-white"
          >
            <option value="SINGLE_CHOICE">Choix unique</option>
            <option value="MULTIPLE_CHOICE">Choix multiple</option>
          </select>
        </div>
        <button
          onClick={onRemove}
          className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Question text */}
      <input
        type="text"
        value={question.text}
        onChange={e => onUpdate("text", e.target.value)}
        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-3"
        placeholder="Texte de la question *"
      />

      {/* Options */}
      <div className="space-y-2 mb-3">
        <label className="text-xs text-slate-500">Options de réponse</label>
        {question.options.map((option, optIndex) => (
          <div key={optIndex} className="flex items-center gap-2">
            <input
              type="text"
              value={option}
              onChange={e => onUpdateOption(optIndex, e.target.value)}
              className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
              placeholder={`Option ${optIndex + 1}`}
            />
            <button
              onClick={() => onUpdate("correctAnswer", option)}
              className={`p-1.5 rounded ${
                question.correctAnswer === option
                  ? "bg-green-100 text-green-600"
                  : "text-slate-400 hover:text-green-600 hover:bg-green-50"
              }`}
              title="Bonne réponse"
            >
              <CheckCircle className="w-4 h-4" />
            </button>
            <button
              onClick={() => onUpdate("alertOnAnswer", question.alertOnAnswer === option ? null : option)}
              className={`p-1.5 rounded ${
                question.alertOnAnswer === option
                  ? "bg-amber-100 text-amber-600"
                  : "text-slate-400 hover:text-amber-600 hover:bg-amber-50"
              }`}
              title="Déclencher alerte"
            >
              <AlertTriangle className="w-4 h-4" />
            </button>
            {question.options.length > 2 && (
              <button
                onClick={() => onRemoveOption(optIndex)}
                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
        <button
          onClick={onAddOption}
          className="text-xs text-blue-600 hover:underline flex items-center gap-1"
        >
          <Plus className="w-3 h-3" />
          Ajouter une option
        </button>
      </div>

      {/* Legend */}
      <div className="flex gap-4 text-xs text-slate-500 pt-2 border-t border-slate-200">
        <span className="flex items-center gap-1">
          <CheckCircle className="w-3 h-3 text-green-500" />
          Bonne réponse
        </span>
        <span className="flex items-center gap-1">
          <AlertTriangle className="w-3 h-3 text-amber-500" />
          Déclenche alerte IDE
        </span>
      </div>
    </div>
  );
}
